import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { Sparkles, Send, X, Loader2, Check, Undo2, MessageSquareText } from "lucide-react";

const SUGGESTIONS = [
  "Make Day 2 cheaper",
  "Replace a museum with something outdoors",
  "Move dinner closer to the hotel",
  "Add more downtime on the busiest day",
];

export const TripAssistant = ({ trip, onTripChange }) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I know your whole itinerary, budget and preferences. Ask me to tweak anything — I'll show you what changes before applying it." },
  ]);
  const [pending, setPending] = useState(null); // {reply, changes, updated_itinerary}
  const sessionId = useRef(`assist-${Math.random().toString(36).slice(2)}`);
  const scrollRef = useRef();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pending, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setPending(null);
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/trips/assistant", {
        trip,
        message: msg,
        session_id: sessionId.current,
      });
      setMessages((m) => [...m, { role: "assistant", text: data.reply || "Done." }]);
      if (data.action === "modify" && (data.updated_itinerary || data.updated_accommodations || data.updated_restaurants)) {
        setPending({
          changes: data.changes || [],
          updated_itinerary: data.updated_itinerary,
          updated_accommodations: data.updated_accommodations,
          updated_restaurants: data.updated_restaurants,
        });
      }
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry — I couldn't process that. Please try rephrasing." }]);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Assistant error");
    } finally {
      setLoading(false);
    }
  };

  const applyChanges = () => {
    if (!pending) return;
    const next = { ...trip };
    if (pending.updated_itinerary) next.itinerary = pending.updated_itinerary;
    if (pending.updated_accommodations) next.accommodations = pending.updated_accommodations;
    if (pending.updated_restaurants) next.restaurants = pending.updated_restaurants;
    onTripChange?.(next, { label: "AI assistant" });
    setMessages((m) => [...m, { role: "assistant", text: "Applied ✓ — you can undo this from the History menu at any time." }]);
    setPending(null);
    toast.success("Trip updated");
  };

  return (
    <>
      {!open && (
        <button
          data-testid="assistant-open-btn"
          onClick={() => setOpen(true)}
          className="no-print fixed bottom-6 right-6 z-[900] inline-flex items-center gap-2 px-5 py-3.5 rounded-full bg-[#1A3B2B] text-[#FDFBF7] shadow-xl hover:bg-[#12291e] transition-colors"
        >
          <Sparkles className="w-5 h-5 text-[#C8553D]" /> Ask WanderlyMe
        </button>
      )}
      {open && (
        <div
          data-testid="assistant-panel"
          className="no-print fixed bottom-4 right-4 z-[900] w-[min(420px,calc(100vw-2rem))] h-[min(600px,calc(100vh-2rem))] flex flex-col rounded-3xl border border-black/10 bg-[#FDFBF7] shadow-2xl overflow-hidden"
        >
          <div className="flex items-center justify-between px-5 py-3.5 bg-[#1A3B2B] text-[#FDFBF7]">
            <span className="inline-flex items-center gap-2 font-display text-lg">
              <Sparkles className="w-5 h-5 text-[#C8553D]" /> Trip assistant
            </span>
            <button data-testid="assistant-close-btn" onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/15">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`assistant-msg-${m.role}`}
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user" ? "bg-[#C8553D] text-[#FDFBF7] rounded-br-sm" : "bg-white border border-black/5 rounded-bl-sm"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}

            {pending && (
              <div data-testid="assistant-change-preview" className="rounded-2xl border border-[#C8553D]/40 bg-[#C8553D]/5 p-4">
                <p className="text-xs uppercase tracking-widest text-[#C8553D] mb-2 flex items-center gap-1.5">
                  <MessageSquareText className="w-3.5 h-3.5" /> Proposed changes
                </p>
                {pending.changes.length ? (
                  <ul className="space-y-1.5 mb-3">
                    {pending.changes.map((c, i) => (
                      <li key={i} className="text-sm flex gap-2 items-start">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#C8553D] shrink-0" /> {c}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm mb-3">I've prepared an updated itinerary.</p>
                )}
                <div className="flex gap-2">
                  <button
                    data-testid="assistant-apply-btn"
                    onClick={applyChanges}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1A3B2B] text-[#FDFBF7] text-sm hover:bg-[#12291e] transition-colors"
                  >
                    <Check className="w-4 h-4" /> Apply
                  </button>
                  <button
                    data-testid="assistant-discard-btn"
                    onClick={() => setPending(null)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-black/15 text-sm hover:bg-black/5 transition-colors"
                  >
                    <Undo2 className="w-4 h-4" /> Discard
                  </button>
                </div>
              </div>
            )}

            {loading && (
              <div className="flex justify-start">
                <div className="px-3.5 py-2.5 rounded-2xl bg-white border border-black/5">
                  <Loader2 className="w-4 h-4 animate-spin text-[#C8553D]" />
                </div>
              </div>
            )}
          </div>

          {messages.length <= 1 && !pending && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  data-testid="assistant-suggestion"
                  onClick={() => send(s)}
                  className="text-xs px-3 py-1.5 rounded-full border border-black/10 bg-white hover:border-[#C8553D] transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <div className="p-3 border-t border-black/5 flex items-center gap-2">
            <input
              data-testid="assistant-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask to change your trip…"
              className="flex-1 text-sm rounded-full border border-black/10 bg-white px-4 py-2.5 outline-none focus:border-[#1A3B2B] transition-colors"
            />
            <button
              data-testid="assistant-send-btn"
              onClick={() => send()}
              disabled={loading || !input.trim()}
              className="p-2.5 rounded-full bg-[#C8553D] text-[#FDFBF7] hover:bg-[#a8412f] transition-colors disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
